import { ref, type Ref } from 'vue'
import { type AxiosInstance, createAxiosClientWithAladinBearer } from '@b2ag/axios'
import { getConfig } from '@b2ag/micro-front-tools'
import { getCurrentAppBrand } from '@b2ag/app-brands'
import { buildCallerId } from '@b2ag/utils/src/caller-id'
import FeatureFlippingService from './featureFlipping.service'

let isProductViewedEnabled: Ref<boolean>
let isNewSearchEnabled: Ref<boolean>
let isAnchoredArticlesEnabled: Ref<boolean>
let isSearchBarPrevisionnelEnabled: Ref<boolean>
let isShippingAddressEnabled: Ref<boolean>
let isRealtimeStocksEnabled: Ref<boolean>
let isPrestaServiceEnabled: Ref<boolean>
let isReorderDisabled: Ref<boolean>
let isSimilarProductsEnabled: Ref<boolean>
let isFrequentlyBoughtTogetherEnabled: Ref<boolean>
let isCustomFiltersEnabled: Ref<boolean>
let isDefaultCustomFiltersEnabled: Ref<boolean>
let isPaymentValidationByLinkEnabled: Ref<boolean>
let isPaymentValidationByCodeEnabled: Ref<boolean>
let isCheckoutV2Enabled: Ref<boolean>
let features: Record<string, Ref>
let featureFlippingService: FeatureFlippingService | undefined
let isNewConfigurationEnabled: Ref<boolean>
let isNewConfigurationDevEnabled: Ref<boolean>
let isSalesAgreementEnabled: Ref<boolean>
let isSearchStockV2Enabled: Ref<boolean>

/**
 * @deprecated For unit test only
 */
export function reset() {
  featureFlippingService = undefined
}

export function provideFeatureFlipping(
  featureFlippingApiClient: Pick<AxiosInstance, 'post'>,
  appFront: string,
  appBrand: string = 'aladin',
) {
  featureFlippingService = new FeatureFlippingService(featureFlippingApiClient, appBrand)

  isProductViewedEnabled = ref(false)
  isNewSearchEnabled = ref(false)
  isAnchoredArticlesEnabled = ref(false)
  isSearchBarPrevisionnelEnabled = ref(false)
  isShippingAddressEnabled = ref(false)
  isRealtimeStocksEnabled = ref(false)
  isPrestaServiceEnabled = ref(false)
  isReorderDisabled = ref(false)
  isSimilarProductsEnabled = ref(false)
  isFrequentlyBoughtTogetherEnabled = ref(false)
  isCustomFiltersEnabled = ref(false)
  isDefaultCustomFiltersEnabled = ref(false)
  isNewConfigurationEnabled = ref(false)
  isNewConfigurationDevEnabled = ref(false)
  isPaymentValidationByLinkEnabled = ref(false)
  isPaymentValidationByCodeEnabled = ref(false)
  isSalesAgreementEnabled = ref(false)
  isCheckoutV2Enabled = ref(false)
  isSearchStockV2Enabled = ref(false)

  features = {
    [`${appBrand}:front-tech:products-viewed`]: isProductViewedEnabled,
    [`${appBrand}:front-tech:taskforce-new-search`]: isNewSearchEnabled,
    [`${appBrand}:article:anchored-articles`]: isAnchoredArticlesEnabled,
    [`${appBrand}:previsionnel:search-bar`]: isSearchBarPrevisionnelEnabled,
    [`${appBrand}:shipping-address`]: isShippingAddressEnabled,
    [`${appBrand}:front-${appFront}:realtime-stocks`]: isRealtimeStocksEnabled,
    [`${appBrand}:presta-service`]: isPrestaServiceEnabled,
    [`${appBrand}:front-${appFront}:disable-reorder`]: isReorderDisabled,
    [`${appBrand}:similar-products-from-algolia`]: isSimilarProductsEnabled,
    [`${appBrand}:frequently-bought-products-from-algolia`]: isFrequentlyBoughtTogetherEnabled,
    [`${appBrand}:custom-filters-configuration`]: isCustomFiltersEnabled,
    [`${appBrand}:default-custom-filters-configuration`]: isDefaultCustomFiltersEnabled,
    [`${appBrand}:new-configuration`]: isNewConfigurationEnabled,
    [`${appBrand}:new-configuration-dev`]: isNewConfigurationDevEnabled,
    [`${appBrand}:payment:validation-by-link`]: isPaymentValidationByLinkEnabled,
    [`${appBrand}:payment:validation-by-code`]: isPaymentValidationByCodeEnabled,
    [`${appBrand}:checkout-v2`]: isCheckoutV2Enabled,
    [`${appBrand}:search-stocks-v2`]: isSearchStockV2Enabled,
    [`${appBrand}:front-${appFront}:sales-agreement`]: isSalesAgreementEnabled,
  }
}

export interface FeatureFlipping {
  isDashboardTcEnabled: boolean
  isProductViewedEnabled: Ref<boolean>
  isNewSearchEnabled: Ref<boolean>
  updateFeatureFlipping: (partnerId?: number, membershipCode?: string) => Promise<void>
  isAnchoredArticlesEnabled: Ref<boolean>
  isSearchBarPrevisionnelEnabled: Ref<boolean>
  isShippingAddressEnabled: Ref<boolean>
  isRealtimeStocksEnabled: Ref<boolean>
  isPrestaServiceEnabled: Ref<boolean>
  isReorderDisabled: Ref<boolean>
  isPortalEntriesEnabled: Ref<boolean>
  isSimilarProductsEnabled: Ref<boolean>
  isFrequentlyBoughtTogetherEnabled: Ref<boolean>
  isCopilotArcDashboardEnabled: Ref<boolean>
  isFilterOrdersByInvoicesEnabled: Ref<boolean>
  isCustomFiltersEnabled: Ref<boolean>
  isDefaultCustomFiltersEnabled: Ref<boolean>
  isNewConfigurationEnabled: Ref<boolean>
  isNewConfigurationDevEnabled: Ref<boolean>
  isPaymentValidationByLinkEnabled: Ref<boolean>
  isPaymentValidationByCodeEnabled: Ref<boolean>
  isCheckoutV2Enabled: Ref<boolean>
  isSalesAgreementEnabled: Ref<boolean>
  isSearchStockV2Enabled: Ref<boolean>
}

export function useFeatureFlipping(): FeatureFlipping {
  return {
    isDashboardTcEnabled: getConfig('DASHBOARD_CA_TC_FEATURE') === 'yes',
    get isProductViewedEnabled() {
      return isProductViewedEnabled
    },
    get isNewSearchEnabled() {
      return isNewSearchEnabled
    },
    get isAnchoredArticlesEnabled() {
      return isAnchoredArticlesEnabled
    },
    get isSearchBarPrevisionnelEnabled() {
      return isSearchBarPrevisionnelEnabled
    },
    get isShippingAddressEnabled() {
      return isShippingAddressEnabled
    },
    get isRealtimeStocksEnabled() {
      return isRealtimeStocksEnabled
    },
    get isPrestaServiceEnabled() {
      return isPrestaServiceEnabled
    },
    get isReorderDisabled() {
      return isReorderDisabled
    },
    get isPortalEntriesEnabled() {
      return ref(getConfig('PORTAL_ENTRIES_ENABLED'))
    },
    get isSimilarProductsEnabled() {
      return isSimilarProductsEnabled
    },
    get isFrequentlyBoughtTogetherEnabled() {
      return isFrequentlyBoughtTogetherEnabled
    },
    get isCopilotArcDashboardEnabled() {
      return ref(getConfig('COPILOT_ARC_DASHBOARD_ENABLED') === 'yes')
    },
    get isFilterOrdersByInvoicesEnabled() {
      return ref(getConfig('FILTER_ORDERS_BY_INVOICES_FEATURE') === 'yes')
    },
    get isCustomFiltersEnabled() {
      return isCustomFiltersEnabled
    },
    get isDefaultCustomFiltersEnabled() {
      return isDefaultCustomFiltersEnabled
    },
    get isPaymentValidationByLinkEnabled() {
      return isPaymentValidationByLinkEnabled
    },
    get isPaymentValidationByCodeEnabled() {
      return isPaymentValidationByCodeEnabled
    },
    get isNewConfigurationEnabled() {
      return isNewConfigurationEnabled
    },
    get isNewConfigurationDevEnabled() {
      return isNewConfigurationDevEnabled
    },
    get isCheckoutV2Enabled() {
      return isCheckoutV2Enabled
    },
    get isSalesAgreementEnabled() {
      return isSalesAgreementEnabled
    },
    get isSearchStockV2Enabled() {
      return isSearchStockV2Enabled
    },
    updateFeatureFlipping: async (partnerId?: number, membershipCode?: string) => {
      if (!featureFlippingService) throw new Error('FeatureFlippingService is not initialized')

      try {
        // Temporary call, don't forget to remove when all cooperative will be migrated
        if (partnerId && membershipCode) {
          const api = createAxiosClientWithAladinBearer(getConfig('PRODUCT_API_HOST'), { callerId: buildCallerId() })
          const { data } = await api.get(`/v1/offers/${partnerId}/configuration-type`, {
            headers: {
              'X-Beneficiary-Id': membershipCode,
            },
          })
          const { type } = data
          features[`${getCurrentAppBrand()}:new-configuration`].value = type === 'new'
          features[`${getCurrentAppBrand()}:new-configuration-dev`].value =
            type === 'new' && +partnerId === 8 && getConfig('ENVIRONMENT_NAME') === 'QUA'
        }
        // End of temporary call
        const checkAllResponse = await featureFlippingService.checkAllFeatures(partnerId)
        Object.keys(features).forEach((featureKey) => {
          const checkFeature = checkAllResponse.find((feature) => feature.id === featureKey)
          if (
            featureKey === `${getCurrentAppBrand()}:new-configuration` ||
            featureKey === `${getCurrentAppBrand()}:new-configuration-dev`
          )
            return
          features[featureKey].value = checkFeature ? checkFeature.active : false
        })
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
  }
}
